/** @jsx jsx */
import { Global, css, jsx } from '@emotion/react';

import * as React from 'react';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/initialize';

import user from 'js/lib/user';
import { MOMENT_FORMATS, momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';

import { COMMON_DATE_FORMAT } from 'bundles/enterprise-admin-user-filters/utils';
import { Spacer } from 'bundles/enterprise-ui';

// eslint-disable-next-line no-restricted-syntax
import 'css!react-dates/lib/css/_datepicker.css';

const DateField: React.VFC<{
  label: string;
  value?: string;
  onChange: (value: string) => void;
  validationStatus?: 'error';
  validationLabel?: string;
  className?: string;
}> = ({ label, value: initialValue, validationStatus, validationLabel, onChange, ...props }) => {
  const [focused, setFocus] = React.useState(false);
  const [value, setValue] = React.useState(initialValue ?? '');
  const date = value ? momentWithUserTimezone(value, user.getUserTimezone()) : null;
  const hasError = validationStatus === 'error';
  return (
    <div {...props} className={props.className + (hasError ? ' SingleDatePickerInput__hasError' : '')}>
      <Typography2 component="h3" variant="subtitleMedium">
        {label}
      </Typography2>
      <Spacer size={16} />
      {hasError && (
        <Typography2
          component="p"
          variant="bodySecondary"
          color="error"
          css={css`
            font-weight: 700;
            background-color: rgb(253 245 245);
            border-radius: 4px;
            padding: 4px 8px;
          `}
        >
          {validationLabel}
        </Typography2>
      )}
      <Spacer size={8} />
      <SingleDatePicker
        id="date-field"
        focused={focused}
        onFocusChange={() => setFocus(!focused)}
        placeholder={COMMON_DATE_FORMAT}
        showClearDate
        inputIconPosition="after"
        openDirection="down"
        numberOfMonths={1}
        hideKeyboardShortcutsPanel
        displayFormat={COMMON_DATE_FORMAT}
        date={date}
        onDateChange={(newDate) => {
          const newValue = newDate ? newDate.format(MOMENT_FORMATS.YEAR_MONTH_DAY) : undefined;

          setValue(newValue ?? '');
          onChange?.(newValue ?? '');
        }}
        isOutsideRange={() => false}
      />
      <Global
        styles={css`
          .SingleDatePickerInput__withBorder {
            border-color: #636363;
            border-radius: 4px;
            overflow: hidden;
            min-width: 200px;
          }

          .SingleDatePickerInput__withBorder input,
          .SingleDatePickerInput__withBorder input::placeholder {
            font-size: 1rem;
          }

          .SingleDatePickerInput__hasError .SingleDatePickerInput__withBorder {
            border-color: rgb(211 0 1);
          }

          .SingleDatePickerInput_clearDate {
            margin-top: -4px;
          }

          .DayPicker_transitionContainer {
            min-height: 300px;
          }
        `}
      />
    </div>
  );
};
export default DateField;
